import { Collection, getMongoDb } from "@swoft/persistence";
import { 
    IPackageMappingRepository, 
    PackageMappingFilters, 
    PackageMappingStatistics 
} from '../../domain/ports/IPackageMappingRepository';
import { PackageMapping, PackageType, MappingStatus } from '../../domain/entities/PackageMapping';
import { getDDDCollection, DDD_COLLECTIONS } from '../../../../config/collections';

/**
 * MongoDB implementation of Package Mapping Repository
 * 
 * Handles persistence of package-to-domain mappings in MongoDB.
 * Implements the Repository pattern for Clean Architecture.
 */
export class MongoPackageMappingRepository implements IPackageMappingRepository {
    private readonly collectionName = DDD_COLLECTIONS.PACKAGE_MAPPINGS;
    private collection?: Collection<PackageMappingDocument>;

    private getCollection(): Collection<PackageMappingDocument> {
        if (!this.collection) {
            const db = getMongoDb();
            this.collection = db.collection<PackageMappingDocument>(this.collectionName);
        }
        return this.collection;
    }

    async save(mapping: PackageMapping): Promise<void> {
        const document = this.toDocument(mapping);
        
        const collection = this.getCollection();
        await collection.replaceOne(
            { packageName: mapping.packageName },
            document,
            { upsert: true }
        );
    }

    async findByPackageName(packageName: string): Promise<PackageMapping | null> {
        const collection = this.getCollection();
        const document = await collection.findOne({ packageName });
        return document ? this.toDomain(document) : null;
    }

    async findByDomainId(domainId: string): Promise<PackageMapping[]> {
        const documents = await this.collection.find({ domainId }).toArray();
        return documents.map(doc => this.toDomain(doc));
    }

    async findByStatus(status: MappingStatus): Promise<PackageMapping[]> {
        const documents = await this.collection.find({ status }).toArray();
        return documents.map(doc => this.toDomain(doc));
    }

    async findByPackageType(packageType: PackageType): Promise<PackageMapping[]> {
        const documents = await this.collection.find({ packageType }).toArray();
        return documents.map(doc => this.toDomain(doc));
    }

    async findAll(filters: PackageMappingFilters): Promise<{
        mappings: PackageMapping[];
        total: number;
    }> {
        // Build MongoDB query
        const query: any = {};
        
        if (filters.domainId) {
            query.domainId = filters.domainId;
        }
        
        if (filters.status) {
            query.status = filters.status;
        }
        
        if (filters.packageType) {
            query.packageType = filters.packageType;
        }
        
        if (filters.search) {
            query.packageName = { $regex: filters.search, $options: 'i' };
        }

        // Get total count
        const total = await this.collection.countDocuments(query);

        // Get paginated results
        const documents = await this.collection
            .find(query)
            .sort({ updatedAt: -1 })
            .skip(filters.offset || 0)
            .limit(filters.limit || 50)
            .toArray();

        const mappings = documents.map(doc => this.toDomain(doc));

        return { mappings, total };
    }

    async deleteByPackageName(packageName: string): Promise<void> {
        await this.collection.deleteOne({ packageName });
    }

    async bulkSave(mappings: PackageMapping[]): Promise<void> {
        if (mappings.length === 0) return;

        const operations = mappings.map(mapping => ({
            replaceOne: {
                filter: { packageName: mapping.packageName },
                replacement: this.toDocument(mapping),
                upsert: true
            }
        }));

        await this.collection.bulkWrite(operations);
    }

    async exists(packageName: string): Promise<boolean> {
        const count = await this.collection.countDocuments({ packageName });
        return count > 0;
    }

    async getStatistics(): Promise<PackageMappingStatistics> {
        const pipeline = [
            {
                $group: {
                    _id: null,
                    totalMappings: { $sum: 1 },
                    alignedCount: {
                        $sum: { $cond: [{ $eq: ['$status', 'aligned'] }, 1, 0] }
                    },
                    orphanedCount: {
                        $sum: { $cond: [{ $eq: ['$status', 'orphaned'] }, 1, 0] }
                    },
                    needsReviewCount: {
                        $sum: { $cond: [{ $eq: ['$status', 'needs_review'] }, 1, 0] }
                    },
                    disputedCount: {
                        $sum: { $cond: [{ $eq: ['$status', 'disputed'] }, 1, 0] }
                    },
                    averageConfidence: { $avg: '$classification.confidence' }
                }
            }
        ];

        const [stats] = await this.collection.aggregate(pipeline).toArray();

        // Get package type distribution
        const typeDistribution = await this.collection.aggregate([
            {
                $group: {
                    _id: '$packageType',
                    count: { $sum: 1 }
                }
            }
        ]).toArray();

        const byPackageType: Record<PackageType, number> = {
            [PackageType.DOMAIN]: 0,
            [PackageType.API]: 0,
            [PackageType.SERVICE]: 0,
            [PackageType.APP]: 0,
            [PackageType.SHARED]: 0,
            [PackageType.INFRASTRUCTURE]: 0
        };
        
        typeDistribution.forEach(item => {
            if (item._id && Object.values(PackageType).includes(item._id as PackageType)) {
                byPackageType[item._id as PackageType] = item.count;
            }
        });

        return {
            totalMappings: stats?.totalMappings || 0,
            alignedCount: stats?.alignedCount || 0,
            orphanedCount: stats?.orphanedCount || 0,
            needsReviewCount: stats?.needsReviewCount || 0,
            disputedCount: stats?.disputedCount || 0,
            byPackageType,
            averageConfidence: stats?.averageConfidence || 0
        };
    }

    // Private mapping methods
    private toDocument(mapping: PackageMapping): PackageMappingDocument {
        return {
            _id: mapping.id,
            packageName: mapping.packageName,
            packageType: mapping.packageType,
            domainId: mapping.domainId,
            classification: mapping.classification,
            status: mapping.status,
            createdAt: mapping.createdAt,
            updatedAt: mapping.updatedAt,
            lastReviewedBy: mapping.lastReviewedBy,
            notes: mapping.notes
        };
    }

    private toDomain(document: PackageMappingDocument): PackageMapping {
        return PackageMapping.reconstitute(
            document._id,
            document.packageName,
            document.packageType,
            document.domainId,
            document.classification,
            document.status,
            document.createdAt,
            document.updatedAt,
            document.lastReviewedBy || undefined,
            document.notes || undefined
        );
    }
}

// MongoDB document interface
interface PackageMappingDocument {
    _id: string;
    packageName: string;
    packageType: PackageType;
    domainId: string | null;
    classification: {
        confidence: number;
        reasoning: string;
        suggestedDomain?: string | null;
        automaticallyClassified: boolean;
    };
    status: MappingStatus;
    createdAt: Date;
    updatedAt: Date;
    lastReviewedBy?: string | null;
    notes?: string | null;
}